import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRightIcon, 
  CheckIcon,
  StarIcon,
  SparklesIcon,
  PaintBrushIcon,
  GiftIcon,
  UserIcon,
  PrinterIcon,
  PlayIcon,
  UsersIcon,
  TrophyIcon,
  ClockIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const Home: React.FC = () => {
  const services = [
    {
      icon: <PaintBrushIcon className="h-8 w-8 text-accent" />,
      title: 'Printing & Advertising',
      description: 'High-quality printing services for all your marketing materials, banners, and promotional items.'
    },
    {
      icon: <SparklesIcon className="h-8 w-8 text-accent" />,
      title: 'Branding & Signage',
      description: 'Professional branding solutions including logos, signage, and corporate identity packages.'
    },
    {
      icon: <UserIcon className="h-8 w-8 text-accent" />,
      title: 'Corporate Wear & Uniforms',
      description: 'Custom corporate wear and uniforms designed to represent your brand professionally.'
    },
    {
      icon: <GiftIcon className="h-8 w-8 text-accent" />,
      title: 'Corporate Gifts',
      description: 'Thoughtful corporate gifts and promotional items that leave a lasting impression.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mupfumi',
      company: 'Tech Solutions Zimbabwe',
      content: 'Electus Creative gave us everything we needed — fast, professional, and with attention to detail. Our branded materials exceeded our expectations.',
      rating: 5
    },
    {
      name: 'John Chikwanda',
      company: 'Harare Holdings',
      content: 'Our branded corporate gifts were loved by all our clients — amazing quality! The team understood our vision perfectly.',
      rating: 5
    },
    {
      name: 'Grace Mutambara',
      company: 'Innovate Africa',
      content: 'From concept to delivery, Electus Creative made our corporate rebrand seamless. Highly recommended for any branding needs.',
      rating: 5
    }
  ];

  const stats = [
    { number: '500+', label: 'Happy Clients' },
    { number: '1000+', label: 'Projects Completed' },
    { number: '5+', label: 'Years Experience' },
    { number: '24/7', label: 'Customer Support' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-white pt-20 pb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-gray-900">
              <div className="flex items-center mb-4">
                <PlayIcon className="h-5 w-5 text-accent mr-2" />
                <span className="text-accent font-medium text-sm uppercase tracking-wide">Elevate Your Brand With Us</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Empowering Your Success with{' '}
                <span className="text-primary">Creative Expertise</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We deliver high-quality branding, advertising, corporate gifts, and corporate wear 
                designed to help your business stand out in the competitive Zimbabwean market. 
                Our comprehensive solutions drive growth and build lasting brand identities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/gallery"
                  className="bg-primary text-white hover:bg-primary-dark text-lg px-8 py-4 inline-flex items-center justify-center group rounded-lg font-medium transition-colors duration-200"
                >
                  Browse Products
                  <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-gray-300 text-gray-700 hover:border-primary hover:text-primary text-lg px-8 py-4 inline-flex items-center justify-center rounded-lg font-medium transition-colors duration-200"
                >
                  Our Services
                </Link>
              </div>
            </div>
                <div className="relative">
                  {/* Service-Related Images */}
                  <div className="relative">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="rounded-lg h-48 relative overflow-hidden">
                        <img 
                          src="/media/corpbranding.jpg?v=2" 
                          alt="Corporate Branding & Signage" 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30"></div>
                        <span className="absolute bottom-2 left-2 text-white text-xs font-medium z-10">Branding</span>
                      </div>
                      <div className="rounded-lg h-48 relative overflow-hidden mt-8">
                        <img 
                          src="/media/corpwear.jpg" 
                          alt="Corporate Wear" 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30"></div>
                        <span className="absolute bottom-2 left-2 text-white text-xs font-medium z-10">Corporate Wear</span>
                      </div>
                      <div className="rounded-lg h-48 relative overflow-hidden">
                        <img 
                          src="/media/bag.jpg" 
                          alt="Corporate Gifts" 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30"></div>
                        <span className="absolute bottom-2 left-2 text-white text-xs font-medium z-10">Corporate Gifts</span>
                      </div>
                      <div className="rounded-lg h-48 relative overflow-hidden mt-8">
                        <img 
                          src="/media/mug.jpg" 
                          alt="Printing & Promotional Items" 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30"></div>
                        <span className="absolute bottom-2 left-2 text-white text-xs font-medium z-10">Printing</span>
                      </div>
                    </div>
            
                  </div>
                </div>
          </div>
        </div>
      </section>

      {/* Services Strip */}
      <section className="bg-gradient-to-br from-primary to-primary-dark py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center space-x-8 text-white">
            <span className="text-white text-lg font-medium">Corporate Branding</span>
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-white text-lg font-medium">Corporate Wear & Uniforms</span>
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-white text-lg font-medium">Corporate Gifts</span>
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-white text-lg font-medium">Printing & Advertising</span>
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-white text-lg font-medium">Digital Marketing</span>
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-white text-lg font-medium">Brand Identity</span>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <PlayIcon className="h-5 w-5 text-accent mr-2" />
              <span className="text-accent font-medium text-sm uppercase tracking-wide">About Us</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Empowering Your Success with Creative Expertise
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              {/* Single Business Image */}
              <div className="rounded-lg h-96 relative overflow-hidden">
                <img 
                  src="/media/diary.jpg" 
                  alt="Electus Creative - Your Creative Partner" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary-dark/70 to-accent/80"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white z-10">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-8 mb-6 mx-auto w-32 h-32 flex items-center justify-center">
                      <UsersIcon className="h-16 w-16 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Electus Creative</h3>
                    <p className="text-white/90">Your Creative Partner</p>
                  </div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 right-4 w-20 h-20 bg-white/10 rounded-full"></div>
                <div className="absolute bottom-4 left-4 w-16 h-16 bg-white/10 rounded-full"></div>
                <div className="absolute top-1/2 left-4 w-12 h-12 bg-white/10 rounded-full"></div>
              </div>
            </div>
            
            <div>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Electus Creative is a proudly Zimbabwean company specializing in creative marketing, 
                printing, branding, and corporate identity solutions. We help brands communicate 
                effectively through design, print, and product innovation.
              </p>
              
              {/* Progress Bars */}
              <div className="space-y-6 mb-8">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 font-medium">Marketing & Business Growth</span>
                    <span className="text-primary font-bold">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-accent h-3 rounded-full" style={{width: '85%'}}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 font-medium">Creativity & Innovation</span>
                    <span className="text-primary font-bold">80%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-accent h-3 rounded-full" style={{width: '80%'}}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-700 font-medium">Business & Financial Management</span>
                    <span className="text-primary font-bold">80%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-accent h-3 rounded-full" style={{width: '80%'}}></div>
                  </div>
                </div>
              </div>
              
              <Link
                to="/about"
                className="bg-primary text-white hover:bg-primary-dark px-6 py-3 inline-flex items-center group rounded-lg font-medium transition-colors duration-200"
              >
                About Us
                <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <TrophyIcon className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-primary mb-2">3k+</div>
              <div className="text-gray-600">Successful Projects</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-primary mb-2">200+</div>
              <div className="text-gray-600">Expert Team</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-primary mb-2">350+</div>
              <div className="text-gray-600">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="h-8 w-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-primary mb-2">5+</div>
              <div className="text-gray-600">Years of Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-gradient-to-br from-primary to-primary-dark py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <PlayIcon className="h-5 w-5 text-accent mr-2" />
              <span className="text-accent font-medium text-sm uppercase tracking-wide">Our Services</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Boost Your Brand with Our Expertise
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white rounded-xl p-6">
              <div className="rounded-lg h-48 mb-6 relative overflow-hidden">
                <img 
                  src="/media/branding.jpg?v=3" 
                  alt="Corporate Branding & Logo Design" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30"></div>
                <span className="absolute bottom-2 left-2 text-white text-xs font-medium">Brand Design</span>
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Corporate Branding</h3>
              <p className="text-gray-600 mb-4">
                Professional branding solutions including logos, signage, and corporate identity packages that make your business stand out.
              </p>
              <a href="/services" className="text-accent font-medium inline-flex items-center group">
                Learn more
                <ArrowRightIcon className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
              </a>
            </div>

            {/* Service Card 2 */}
            <div className="bg-accent rounded-xl p-6 text-white">
              <div className="rounded-lg h-48 relative overflow-hidden mb-6">
                <img 
                  src="/media/corpwear2.jpg" 
                  alt="Corporate Wear & Uniforms" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <span className="absolute bottom-2 left-2 text-white text-xs font-medium z-10">Custom Uniforms</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Corporate Wear & Uniforms</h3>
              <p className="mb-4">
                Custom corporate wear and uniforms designed to represent your brand professionally while ensuring comfort and durability.
              </p>
              <a href="/services" className="text-white font-medium inline-flex items-center group">
                Learn more
                <ArrowRightIcon className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
              </a>
            </div>
            
            {/* Service Card 3 */}
            <div className="bg-white rounded-xl p-6">
              <div className="rounded-lg h-48 relative overflow-hidden mb-6">
                <img 
                  src="/media/diary1.jpg" 
                  alt="Corporate Gifts" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <span className="absolute bottom-2 left-2 text-white text-xs font-medium z-10">Premium Gifts</span>
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Corporate Gifts</h3>
              <p className="text-gray-600 mb-4">
                Premium corporate gifts and promotional items that leave a lasting impression on your clients and partners.
              </p>
              <a href="/services" className="text-accent font-medium inline-flex items-center group">
                Learn more
                <ArrowRightIcon className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
              </a>
            </div>
          </div>
              <div className="text-center mt-12">
                <Link
                  to="/gallery"
                  className="bg-white text-primary hover:bg-gray-100 px-6 py-3 inline-flex items-center group rounded-lg font-medium transition-colors duration-200"
                >
                  View All Products
                  <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
              </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's discuss your project and create something amazing together.
          </p>
          <div className="flex justify-center">
            <Link
              to="/contact"
              className="bg-primary text-white hover:bg-primary-dark text-lg px-8 py-4 inline-flex items-center justify-center group rounded-lg font-medium transition-colors duration-200"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
